import { useCallback, useMemo, useRef } from "react"

import { useSurfacePreference } from "../../../../../hooks/use-surface-preference.ts"
import { surfaceKey } from "../../../../User/redux/ui-preferences.types.ts"
import type { ProjectWizardFormData } from "../../../../Project/ProjectAddPage/wizard/project-wizard-data.ts"
import type { PlanSection } from "./PlanStep.tsx"
import type { Destination, WizardStep } from "./wizard-steps.ts"

// What the analysis API has no field for — the step the user was on, a destination
// that is not a record yet, a skipped optional section — kept against the signed-in
// user in the UI-preferences bag, so it survives a reload and another browser.
export interface DraftMemory {
  step?: WizardStep
  destination?: Destination
  /** The section of the review the user was on. */
  planSection?: PlanSection
  /** The New project form as the user left it (dates as YYYY-MM-DD). */
  newProject?: ProjectWizardFormData
  /** The analysis version the form was prefilled from. */
  newProjectFrom?: number
  /** A project this wizard created, kept until it is linked so a retry never creates another. */
  createdProject?: string
  /** The same for a lead. */
  createdLead?: string
  /** The one project type that project was created with. */
  createdProjectType?: string
  /** The memory as it was at the last Save draft, to tell unsaved wizard choices. */
  savedSignature?: string
  at?: number
}

/** The parts of the wizard's memory that are the user's choices, not where they are. */
export const memorySignature = (memory: DraftMemory) =>
  JSON.stringify({
    destination: memory.destination ?? null,
    newProject: memory.newProject ?? null
  })

/** What Save draft keeps with the plan on the server (Discard brings it back). */
export const savedClientState = (memory: DraftMemory) => {
  const { at: _at, savedSignature: _signature, ...state } = memory
  return state as Record<string, unknown>
}

const KEY = surfaceKey("ai-center.fpe-drafts")
const KEEP = 30

export function useWizardMemory(analysisId: string) {
  const [all, store] = useSurfacePreference<Record<string, DraftMemory>>(
    KEY,
    {}
  )
  const memory = useMemo(() => all[analysisId] ?? {}, [all, analysisId])
  // Two calls before the store re-renders (e.g. "created", then "linked") must
  // build on each other, not both on the value this render saw.
  const latest = useRef(all)
  latest.current = all

  const remember = useCallback(
    (patch: Partial<DraftMemory>) => {
      const base = latest.current
      const next = {
        ...base,
        [analysisId]: { ...base[analysisId], ...patch, at: Date.now() }
      }
      // Bounded: the oldest drafts' memories go first.
      const kept = Object.entries(next)
        .sort(([, a], [, b]) => (b.at ?? 0) - (a.at ?? 0))
        .slice(0, KEEP)
      latest.current = Object.fromEntries(kept)
      store(latest.current)
    },
    [analysisId, store]
  )

  // Discard brings back the memory saved with the plan, whole.
  const replace = useCallback(
    (state: DraftMemory) => {
      latest.current = {
        ...latest.current,
        [analysisId]: { ...state, at: Date.now() }
      }
      store(latest.current)
    },
    [analysisId, store]
  )

  const forget = useCallback(() => {
    const { [analysisId]: _gone, ...rest } = latest.current
    latest.current = rest
    store(rest)
  }, [analysisId, store])

  return { memory, remember, replace, forget }
}

export function useDraftMemories() {
  const [all] = useSurfacePreference<Record<string, DraftMemory>>(KEY, {})
  return all
}
